import { AnalyticsService } from "mis-crystal-design-system/analytics";
/** @format */

import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { DrawerService } from "mis-design-system/drawer";
import { ToastService } from "mis-crystal-design-system/toast";
import { combineLatest, Subject, throwError } from "rxjs";
import { catchError, filter, map, take, takeUntil } from "rxjs/operators";
import { CONSTANTS_ASSETS_EMP_WEB_EXP } from "../../constants/assets.constants";
import { NsEmpWebExpData } from "../../models/emp-web-exp.model";
import { MenuDirective } from "../../modules/menu/menu.directive";
import { ApiService } from "../../services/api.service";
import { BookingDataService } from "../../services/booking-data.service";
import { ConfigurationService } from "../../services/configuration.service";
import { HomeDataService } from "../../services/home-data.service";
import { LoaderService } from "../../services/loader.service";
import { WidgetService } from "../../services/widget.service";
import { BookingComponent } from "../modals/booking/booking.component";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "../../services/cookie.service";
import { LANGUAGE_CODE } from "../../constants/cookie.constants";
import { initialsNameGenerator } from "../../functions/booking-utils.functions";
import { colorCodes } from "../../constants/app-colors.constants";

const childRoutes = new Set(["calendar"]);

@Component({
  selector: "mis-employee-web-exp-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"]
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() showBackBtn = false;
  @ViewChild("menuOriginButton", { read: MenuDirective }) menuOrigin: MenuDirective;
  @ViewChild("dropdownButton", { read: MenuDirective }) statusDropdown: MenuDirective;
  @ViewChild("languageSelectionButton", { read: MenuDirective }) languageDropdown: MenuDirective;
  @ViewChild("profileOptionSelection", { read: MenuDirective }) profile: MenuDirective;
  configStream = this.cfgSvc.configurations;
  userInfo = this.homeDataSvc.employeeInfo;
  @Output() gotoHome = new EventEmitter(true);
  Assets = CONSTANTS_ASSETS_EMP_WEB_EXP;
  endObs = new Subject<void>();
  customStatusChangeAllowed = false;
  officeBookingAllowed: boolean = false;
  remoteBookingAllowed: boolean = false;

  currentStatus: NsEmpWebExpData.IEmployeeCustomStatus = {};
  defaultStatusList: NsEmpWebExpData.IEmployeeCustomStatus[] = [];
  displayStatusList: NsEmpWebExpData.IEmployeeCustomStatus[] = [];
  languagesDisplayList: NsEmpWebExpData.ILocaleList[] = [
    {
      name: "Chinese",
      code: "zh"
    },
    {
      name: "Dutch",
      code: "nl"
    },
    {
      name: "English",
      code: "en"
    },
    {
      name: "French",
      code: "fr"
    },
    {
      name: "Italian",
      code: "it"
    },
    {
      name: "Japanese",
      code: "ja"
    },
    {
      name: "Korean",
      code: "ko"
    },
    {
      name: "Portuguese",
      code: "pt"
    },
    {
      name: "Spanish",
      code: "es"
    }
  ];
  initial: { name: string; color: string[] };
  currentLanguage: NsEmpWebExpData.ILocaleList;

  constructor(
    private cfgSvc: ConfigurationService,
    private homeDataSvc: HomeDataService,
    private bookingSvc: BookingDataService,
    private drawerSvc: DrawerService,
    private widgetSvc: WidgetService,
    private toast: ToastService,
    private apiSvc: ApiService,
    private loader: LoaderService,
    private translate: TranslateService,
    private cookieSvc: CookieService,
    private analytics: AnalyticsService,
    public configSvc: ConfigurationService
  ) {}

  setCookie(data: string) {
    const domainSegments = window.location.host.replace(/\:.*/g, "").split(/\./g);
    const mainHost = `${domainSegments.length > 1 ? `.` : ``}${domainSegments.slice(-2).join(".")}`;
    this.cookieSvc.setCookie(LANGUAGE_CODE, data, { Secure: true, path: "/", domain: mainHost });
  }

  ngOnInit(): void {
    this.userInfo.subscribe(emp => {
      if (emp.name) {
        let names = emp.name?.split(" "),
          initials = names?.[0]?.substring(0, 1)?.toUpperCase();
        if (names.length > 1) {
          initials += names?.[names.length - 1]?.substring(0, 1)?.toUpperCase();
        }
        this.initial = {
          name: initials,
          color: colorCodes.sort(() => 0.5 - Math.random()).slice(0, 1)
        };
      }
    });

    this.configStream.pipe(takeUntil(this.endObs)).subscribe(response => {
      this.officeBookingAllowed = response.BUSettings.features[13].createBookingEnabled;
      this.remoteBookingAllowed = response.flattenedSettings.wfhBookingAllowed;

      if (response?.flattenedSettings?.showLanguageOptionInHamburgerMenu) {
        this.homeDataSvc.employeeInfo.pipe(take(1)).subscribe(user => {
          if (user?.preferredLocale) {
            this.translate.use(user?.preferredLocale);
            this.setCookie(user?.preferredLocale);
            this.currentLanguage = this.languagesDisplayList.find(item => item.code === user?.preferredLocale);
            this.languagesDisplayList = this.sortLanguageArray(this.languagesDisplayList, user?.preferredLocale);
          }
        });
      }
      this.customStatusChangeAllowed = response.flattenedSettings.employeeStatusModuleEnabled;
      if (this.customStatusChangeAllowed) {
        combineLatest([
          this.apiSvc.getLastStatus().pipe(
            catchError(err => {
              this.toast.displayMsg(this.toast.messageBuilder.error.simple);
              return throwError(`Failed to load last status. ${JSON.stringify(err)}`);
            })
          ),
          this.apiSvc.getCustomStatusList().pipe(
            catchError(err => {
              this.toast.displayMsg(this.toast.messageBuilder.error.simple);
              return throwError(`Failed to load status. ${JSON.stringify(err)}`);
            })
          ),
          this.widgetSvc.widgetAction.pipe(
            catchError(err => {
              this.toast.displayMsg(this.toast.messageBuilder.error.simple);
              return throwError(`Failed to load user action. ${JSON.stringify(err)}`);
            })
          )
        ])
          .pipe(takeUntil(this.endObs))
          .subscribe(([lastStatus, statusList, checkIn]) => {
            this.loader.show();
            this.defaultStatusList = [];
            this.defaultStatusList.push(...statusList.statusDisplayList);
            if (checkIn === "CHECK-IN") {
              this.setStatus("Online");
            } else if (lastStatus) {
              this.setStatus(lastStatus, true);
            } else {
              this.setStatus("Online");
            }
            this.loader.hide();
          });
      }
    });
  }

  setStatus(statusName: string, fetchLastStatus?: boolean): void {
    let statusObject;
    if (fetchLastStatus) {
      statusObject = this.defaultStatusList.find(item => item.statusName === decodeURIComponent(statusName));
    } else statusObject = this.defaultStatusList.find(item => item.statusName === statusName);
    this.currentStatus = statusObject;
    this.handleStatusChange(statusObject);
  }

  sortLanguageArray(arr: NsEmpWebExpData.ILocaleList[], selectedValue: string) {
    arr = this.sortArrAlphabetically(arr);
    const selectedIndex = arr.findIndex(item => item.code === selectedValue);
    if (selectedIndex !== -1) {
      const selectedObj = arr.splice(selectedIndex, 1)[0];
      arr.unshift(selectedObj);
    }
    return arr;
  }

  sortArrAlphabetically(arr: NsEmpWebExpData.ILocaleList[]) {
    return arr.sort((a, b) => {
      const valueA = a["name"].toLowerCase(); // Convert both values to lowercase for case-insensitive sorting
      const valueB = b["name"].toLowerCase();

      if (valueA < valueB) {
        return -1;
      } else if (valueA > valueB) {
        return 1;
      } else {
        return 0;
      }
    });
  }

  quickActionEvent(act: string): void {
    this.menuOrigin?.close();
    this.analytics.send("emp_web_quick_actions", "emp_web", "click", "");
    if (act === "OFFICE" || act === "WFH") {
      combineLatest([this.cfgSvc.configurations, this.homeDataSvc.employeeInfo])
        .pipe(take(1))
        .subscribe(([configurations, empData]) => {
          const configs = this.bookingSvc.setFormForCreate(configurations, empData, act);
          const ref = this.drawerSvc.show(BookingComponent, configs);
          ref.afterClosed.subscribe(res => {
            if (res === "DONE") {
              this.widgetSvc.refreshWidgets("BOOKINGS");
            }
          });
        });
      if (act === "OFFICE") {
        this.analytics.send("emp_web_wfo_form", "emp_web", "click", "");
      } else {
        this.analytics.send("emp_web_wfh_form", "emp_web", "click", "");
      }
    }
  }
  handleStatusChange(status: NsEmpWebExpData.IEmployeeCustomStatus): void {
    this.loader.show();
    this.apiSvc
      .captureStatusChange(status)
      .pipe(
        catchError(err => {
          this.toast.displayMsg(this.toast.messageBuilder.error.simple);
          return throwError(`Failed to change status. ${JSON.stringify(err)}`);
        })
      )
      .subscribe(response => {
        if (response) {
          this.currentStatus = status;
          this.filterOutSelectedStatus();
        }
        this.loader.hide();
      });
    this.statusDropdown?.close();
  }
  localeSetter(locale: NsEmpWebExpData.ILocaleList): void {
    this.currentLanguage = locale;
    this.languagesDisplayList = this.sortLanguageArray(this.languagesDisplayList, locale.code);
    this.loader.show();
    this.apiSvc.captureLocaleChange(locale?.code).subscribe(localeCode => {
      this.loader.hide();
      if (localeCode) {
        this.translate.use(locale?.code);
        this.setCookie(locale?.code);
        document.querySelector("iframe") && document.querySelector("iframe")?.contentWindow?.postMessage({ reload: true }, "*");
        this.languageDropdown?.close();
      }
    });
  }
  redirectToPreference() {
    // this.profile?.close();
    // let data = { user: this.userInfo, timezone: this.bookingSvc.timezone, closeOnBackDropClick: true };
    // const ref = this.drawerSvc.show(PreferencesContainerComponent, data);
    // ref.afterClosed.subscribe(res => {
    //   if (res === "DONE") {
    //     ref.close();
    //   }
    // });
  }

  back(): void {
    this.gotoHome.emit();
  }

  filterOutSelectedStatus(): void {
    this.displayStatusList = this.defaultStatusList.filter(item => item.statusName !== this.currentStatus.statusName);
  }

  ngOnDestroy(): void {
    this.endObs.next();
    this.endObs.complete();
  }
}
